import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { CAPABILITY_TYPES } from '@homevest/utils/capabilities'
import { EVICTION_STATUSES } from '@homevest/utils/evictions'
import {
  Alert,
  AlertIcon,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useToast
} from '@chakra-ui/react'
import { PencilIcon, ChatBubbleLeftIcon } from '@heroicons/react/20/solid'

import axios from 'lib/axios'
import { hasCapability } from 'lib/admin-perms'
import { formatDateForInput } from 'lib/date-time'
import { Sidepeek } from 'ui'
import { StoreState } from 'store'
import { Eviction } from './Grid'
import EvictionSidepeekDetailsTab from './EvictionSidepeekDetailsTab'
import EvictionSidepeekNotesTab from './EvictionSidepeekNotesTab'
import { EvictionFormData } from './types'

const toDate = (val: string | null | undefined): Date | null => (val ? new Date(val) : null)

const toDateString = (d: Date | null | undefined): string | null =>
  d ? formatDateForInput(d) : null

const toDateTimeString = (d: Date | null | undefined): string | null => (d ? d.toISOString() : null)

interface EvictionSidepeekProps {
  eviction: Eviction | null
  isOpen: boolean
  onClose: () => void
}

type AutoFilledDateFields = {
  completed_at: boolean
  canceled_at: boolean
}

const EMPTY_AUTO_FILLED_DATE_FIELDS: AutoFilledDateFields = {
  completed_at: false,
  canceled_at: false
}

const getInitialFormData = (eviction: Eviction | null): EvictionFormData => ({
  status: eviction?.status ?? '',
  filed_at: toDate(eviction?.filed_at),
  court_date: toDate(eviction?.court_date),
  writ_date: toDate(eviction?.writ_date),
  set_out_date: toDate(eviction?.set_out_date),
  projected_possession_date: toDate(eviction?.projected_possession_date),
  file_sent_to_attorney_at: toDate(eviction?.file_sent_to_attorney_at),
  completed_at: toDate(eviction?.completed_at),
  canceled_at: toDate(eviction?.canceled_at),
  cancelation_reason: eviction?.cancelation_reason ?? '',
  consent_judgement: eviction?.consent_judgement ?? false
})

const getErrorStatusCode = (error?: any) => error?.statusCode ?? error?.status

const getAddFilingFeeErrorMessage = (error?: any) => {
  const statusCode = getErrorStatusCode(error)

  switch (statusCode) {
    case 401:
      return 'You do not have the eviction_manager capability to add a filing fee'
    case 403:
      return 'The rental ledger has been finalized and a filing fee cannot be added'
    default:
      return 'Failed to add filing fee'
  }
}

const getUpdateEvictionErrorMessage = (error?: any) => {
  const statusCode = getErrorStatusCode(error)

  switch (statusCode) {
    case 401:
      return 'You do not have the eviction_manager capability to edit evictions'
    default:
      return 'Failed to update eviction'
  }
}

const getAddNoteErrorMessage = (error?: any) => {
  const statusCode = getErrorStatusCode(error)

  switch (statusCode) {
    case 401:
      return 'You do not have the eviction_manager capability to add eviction notes'
    default:
      return 'Failed to add note'
  }
}

const getEvictionValidationErrorMessage = (formData: EvictionFormData) => {
  if (formData.status === EVICTION_STATUSES.FILED && !formData.filed_at) {
    return 'Filed At is required when status is Filed'
  }

  if (formData.status === EVICTION_STATUSES.CANCELED) {
    if (!formData.canceled_at) {
      return 'Canceled At is required when status is Canceled'
    }

    if (!formData.cancelation_reason) {
      return 'Cancelation Reason is required when status is Canceled'
    }
  }

  if (formData.status === EVICTION_STATUSES.COMPLETED && !formData.completed_at) {
    return 'Completed At is required when status is Completed'
  }

  return null
}

const EvictionSidepeek: React.FC<EvictionSidepeekProps> = ({ eviction, isOpen, onClose }) => {
  const admin = useSelector<StoreState, any>((state) => state.admin)
  const toast = useToast()
  const canManageEvictions = hasCapability(admin, CAPABILITY_TYPES.EVICTION_MANAGER)

  const [formData, setFormData] = useState<EvictionFormData>(getInitialFormData(eviction))
  const [isSaving, setIsSaving] = useState(false)
  const [autoFilledDateFields, setAutoFilledDateFields] = useState<AutoFilledDateFields>(
    EMPTY_AUTO_FILLED_DATE_FIELDS
  )

  const [newNote, setNewNote] = useState('')
  const [isAddingNote, setIsAddingNote] = useState(false)
  const [isConsentJudgementNote, setIsConsentJudgementNote] = useState(false)
  const [filingFeeAmount, setFilingFeeAmount] = useState('')
  const [filingFeeNote, setFilingFeeNote] = useState('')
  const [isAddingFilingFee, setIsAddingFilingFee] = useState(false)

  useEffect(() => {
    setFormData(getInitialFormData(eviction))
    setNewNote('')
    setIsConsentJudgementNote(false)
    setFilingFeeAmount('')
    setFilingFeeNote('')
    setAutoFilledDateFields(EMPTY_AUTO_FILLED_DATE_FIELDS)
  }, [eviction])

  const handleFormDataChange = (nextFormData: EvictionFormData) => {
    let adjustedFormData = nextFormData
    const nextAutoFilledDateFields = { ...autoFilledDateFields }

    if (nextFormData.status !== formData.status) {
      if (nextFormData.status === EVICTION_STATUSES.FILED && !nextFormData.filed_at) {
        adjustedFormData = { ...adjustedFormData, filed_at: new Date() }
      }

      if (nextFormData.status === EVICTION_STATUSES.COMPLETED && !nextFormData.completed_at) {
        adjustedFormData = { ...adjustedFormData, completed_at: new Date() }
        nextAutoFilledDateFields.completed_at = true
      }

      if (nextFormData.status === EVICTION_STATUSES.CANCELED && !nextFormData.canceled_at) {
        adjustedFormData = { ...adjustedFormData, canceled_at: new Date() }
        nextAutoFilledDateFields.canceled_at = true
      }

      if (nextFormData.status === EVICTION_STATUSES.PENDING) {
        adjustedFormData = {
          ...adjustedFormData,
          filed_at: null
        }
      }

      if (nextFormData.status !== EVICTION_STATUSES.COMPLETED) {
        adjustedFormData = { ...adjustedFormData, completed_at: null }
        nextAutoFilledDateFields.completed_at = false
      }

      if (nextFormData.status !== EVICTION_STATUSES.CANCELED) {
        adjustedFormData = {
          ...adjustedFormData,
          canceled_at: null,
          cancelation_reason: ''
        }
        nextAutoFilledDateFields.canceled_at = false
      }
    } else {
      if (nextFormData.completed_at !== formData.completed_at) {
        nextAutoFilledDateFields.completed_at = false
      }

      if (nextFormData.canceled_at !== formData.canceled_at) {
        nextAutoFilledDateFields.canceled_at = false
      }
    }

    setAutoFilledDateFields(nextAutoFilledDateFields)
    setFormData(adjustedFormData)
  }

  const handleSave = async () => {
    if (!eviction || !canManageEvictions) return

    const validationError = getEvictionValidationErrorMessage(formData)

    if (validationError) {
      toast({
        title: validationError,
        status: 'error',
        duration: 5000
      })
      return
    }

    setIsSaving(true)
    try {
      const isCompleted = formData.status === EVICTION_STATUSES.COMPLETED
      const isCanceled = formData.status === EVICTION_STATUSES.CANCELED

      await axios.put(`/admin/evictions/${eviction.id}`, {
        status: formData.status,
        filed_at: toDateTimeString(formData.filed_at),
        court_date: toDateString(formData.court_date),
        writ_date: toDateString(formData.writ_date),
        set_out_date: toDateString(formData.set_out_date),
        projected_possession_date: toDateString(formData.projected_possession_date),
        file_sent_to_attorney_at: toDateTimeString(formData.file_sent_to_attorney_at),
        completed_at: isCompleted ? toDateTimeString(formData.completed_at) : null,
        canceled_at: isCanceled ? toDateTimeString(formData.canceled_at) : null,
        cancelation_reason: isCanceled ? formData.cancelation_reason || null : null,
        consent_judgement: formData.consent_judgement
      })
      toast({ title: 'Eviction updated', status: 'success', duration: 3000 })
      onClose()
    } catch (err: any) {
      toast({
        title: getUpdateEvictionErrorMessage(err),
        status: 'error',
        duration: 5000
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleAddNote = async () => {
    if (!eviction || !newNote.trim() || !canManageEvictions) return
    setIsAddingNote(true)
    try {
      await axios.post(`/admin/evictions/${eviction.id}/notes`, {
        note: newNote.trim(),
        ...(isConsentJudgementNote && { category: 'consent_judgement' })
      })
      toast({ title: 'Note added', status: 'success', duration: 3000 })
      setNewNote('')
      setIsConsentJudgementNote(false)
      onClose()
    } catch (err: any) {
      toast({
        title: getAddNoteErrorMessage(err),
        status: 'error',
        duration: 5000
      })
    } finally {
      setIsAddingNote(false)
    }
  }

  const handleAddFilingFee = async () => {
    if (!eviction || !canManageEvictions || !filingFeeAmount) return

    const amount = Number(filingFeeAmount)
    const trimmedNote = filingFeeNote.trim()

    if (!Number.isFinite(amount) || amount <= 0) {
      return
    }

    setIsAddingFilingFee(true)
    try {
      await axios.post(`/admin/evictions/${eviction.id}/filing-fee`, {
        amount,
        ...(trimmedNote && { note: trimmedNote })
      })
      toast({ title: 'Filing fee added', status: 'success', duration: 3000 })
      setFilingFeeNote('')
      onClose()
    } catch (err: any) {
      toast({
        title: getAddFilingFeeErrorMessage(err),
        status: 'error',
        duration: 5000
      })
    } finally {
      setIsAddingFilingFee(false)
    }
  }

  const address = eviction?.rental?.portfolio_home?.home?.address?.display_line_1
  const tenant = eviction?.rental?.user
    ? `${eviction.rental.user.first_name} ${eviction.rental.user.last_name}`
    : null

  return (
    <Sidepeek
      isOpen={isOpen}
      onClose={onClose}
      title={address ?? 'Eviction'}
      subtitle={tenant ?? undefined}
      closeOnOverlayClick={false}
    >
      {!canManageEvictions && (
        <Alert status='warning' borderRadius='md' mb={4}>
          <AlertIcon />
          You do not have the eviction_manager capability. This eviction is view-only.
        </Alert>
      )}

      <Tabs isFitted variant='solid-rounded' colorScheme='teal'>
        <TabList mb='1em'>
          <Tab>
            <PencilIcon className='mr-1 h-4 w-4' /> Details
          </Tab>
          <Tab>
            <ChatBubbleLeftIcon className='mr-1 h-4 w-4' /> Notes
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <EvictionSidepeekDetailsTab
              eviction={eviction}
              formData={formData}
              canManageEvictions={canManageEvictions}
              isSaving={isSaving}
              filingFeeAmount={filingFeeAmount}
              filingFeeNote={filingFeeNote}
              isAddingFilingFee={isAddingFilingFee}
              onFormDataChange={handleFormDataChange}
              onConsentJudgementChange={(consentJudgement) =>
                setFormData({ ...formData, consent_judgement: consentJudgement })
              }
              onFilingFeeAmountChange={setFilingFeeAmount}
              onFilingFeeNoteChange={setFilingFeeNote}
              onAddFilingFee={handleAddFilingFee}
              onSave={handleSave}
              onClose={onClose}
            />
          </TabPanel>

          <TabPanel>
            <EvictionSidepeekNotesTab
              eviction={eviction}
              canManageEvictions={canManageEvictions}
              newNote={newNote}
              isAddingNote={isAddingNote}
              isConsentJudgementNote={isConsentJudgementNote}
              onNewNoteChange={setNewNote}
              onConsentJudgementNoteChange={setIsConsentJudgementNote}
              onAddNote={handleAddNote}
            />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Sidepeek>
  )
}

export default EvictionSidepeek
